import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Loader2, MoveHorizontal } from "lucide-react";
import { resolveMediaUrl } from "@/lib/imageProxy";

/**
 * 360° körbeforgatható nézegető — kurzor húzással sima scrub (flipbook).
 * frames: string[] — képkockák URL-jei sorrendben (pl. videóból kivágva).
 */
export function Viewer360({
  frames = [],
  sensitivity = 7,
  autoRotate = false,
  rotateIntervalMs = 140,
  className = "",
}) {
  const resolvedFrames = useMemo(
    () => frames.map((src) => resolveMediaUrl(src)),
    [frames],
  );
  const total = resolvedFrames.length;
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [spinning, setSpinning] = useState(autoRotate);
  const [visible, setVisible] = useState(true);
  const containerRef = useRef(null);
  const startX = useRef(0);
  const startIdx = useRef(0);

  useEffect(() => {
    setLoaded(0);
    if (total === 0) return;
    let alive = true;
    let count = 0;
    resolvedFrames.forEach((src) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        if (!alive) return;
        count += 1;
        setLoaded(count);
      };
      img.src = src;
    });
    return () => { alive = false; };
  }, [resolvedFrames, total]);

  useEffect(() => {
    if (!containerRef.current || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio > 0.2),
      { threshold: [0, 0.2, 0.5, 1] },
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const ready = total > 0 && loaded >= total;

  useEffect(() => {
    if (!spinning || !visible || !ready || dragging || total < 2) return;
    const iv = setInterval(() => {
      setIdx((i) => (i + 1) % total);
    }, rotateIntervalMs);
    return () => clearInterval(iv);
  }, [spinning, visible, ready, dragging, total, rotateIntervalMs]);

  const setFromDelta = useCallback((dx) => {
    if (total < 2) return;
    const step = Math.round(dx / sensitivity);
    let next = (startIdx.current - step) % total;
    if (next < 0) next += total;
    setIdx(next);
  }, [sensitivity, total]);

  const stopAutoRotate = () => {
    if (spinning) setSpinning(false);
  };

  const onPointerDown = (e) => {
    if (total < 2) return;
    stopAutoRotate();
    setDragging(true);
    startX.current = e.clientX ?? 0;
    startIdx.current = idx;
    try { containerRef.current?.setPointerCapture?.(e.pointerId); } catch (_) {}
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const x = e.clientX ?? 0;
    setFromDelta(x - startX.current);
  };

  const onPointerUp = (e) => {
    setDragging(false);
    try { containerRef.current?.releasePointerCapture?.(e.pointerId); } catch (_) {}
  };

  const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;

  if (total === 0) return null;

  return (
    <div
      data-testid="viewer-360"
      data-spinning={spinning ? "true" : "false"}
      ref={containerRef}
      className={`relative aspect-[16/10] rounded-3xl overflow-hidden bg-anthracite-900 select-none touch-none ${dragging ? "cursor-grabbing" : "cursor-ew-resize"} ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={(e) => dragging && onPointerUp(e)}
    >
      {resolvedFrames.map((src, i) => (
        <img
          key={`${src}-${i}`}
          src={src}
          alt={`360° nézet – ${i + 1}. kocka`}
          draggable={false}
          className={`absolute inset-0 w-full h-full object-contain ${i === idx ? "opacity-100" : "opacity-0"}`}
          style={{ transition: dragging ? "none" : "opacity 0.06s linear" }}
        />
      ))}

      {!ready && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-anthracite-900/70 backdrop-blur-sm z-10">
          <Loader2 className="w-8 h-8 text-electric animate-spin" />
          <div className="mt-3 text-xs text-slate-300">360° előkészítés – {progress}%</div>
        </div>
      )}

      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md text-xs text-white pointer-events-none">
        <MoveHorizontal className="w-3.5 h-3.5 text-electric" strokeWidth={1.75} />
        <span data-testid="viewer-360-hint">Húzd balra-jobbra a forgatáshoz</span>
      </div>

      <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-md text-[11px] tracking-wider text-slate-200 pointer-events-none">
        <span data-testid="viewer-360-frame">{idx + 1}</span> / {total}
      </div>
    </div>
  );
}

/** Legacy URL lista — nem angle dict. */
export function isFrameListFormat(images_360) {
  return Array.isArray(images_360) && images_360.length > 0 && typeof images_360[0] === "string";
}
